/**
 * Vocabulário das seções dos detalhamentos de protocolo: ícone, subtítulo e se a seção descreve
 * DADOS (campos) ou identificação/resumo (texto).
 *
 * Existe porque os mesmos títulos se repetem entre as telas — "Protocolo" aparece em 20 delas,
 * "Dados do Solicitante" em 12, "Pagamento" em 9. Com a decisão dentro de cada componente, a mesma
 * seção nascia com ícone e descrição diferentes conforme o serviço, e cada tela nova recomeçava a
 * escolha do zero. Aqui o título é a chave: quem repete o nome herda a mesma apresentação.
 *
 * A chave é normalizada (sem acento, sem caixa) porque os cadastros divergem na acentuação — há
 * "Endereço" e "Endereco", "Licenças e Alvará" e "Licencas e Alvara", para a mesma seção.
 */

/** Ícone e descrição de uma seção. */
interface ApresentacaoDaSecao {
    icone: string;
    subtitulo: string;
    /**
     * Seção de identificação ou de resumo, cujos campos seguem como TEXTO. Ausente na maioria: o
     * padrão é a seção descrever dados do protocolo, e esses saem como campos.
     */
    somenteLeituraEmTexto?: boolean;
}

const PADRAO: ApresentacaoDaSecao = { icone: 'pi pi-list', subtitulo: '' };

/** Título normalizado: sem acento, sem caixa e sem espaço sobrando. */
function chave(titulo: string): string {
    return (titulo ?? '')
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');
}

const APRESENTACAO: Record<string, ApresentacaoDaSecao> = {
    // Identificação e resumo: seguem como texto — número de protocolo não se corrige num campo, e a
    // situação precisa do selo colorido que o modo texto desenha.
    protocolo: { icone: 'pi pi-hashtag', subtitulo: 'Número, data e situação da solicitação', somenteLeituraEmTexto: true },
    solicitacao: { icone: 'pi pi-hashtag', subtitulo: 'Identificação e situação da solicitação', somenteLeituraEmTexto: true },
    'resumo de estruturas': { icone: 'pi pi-chart-bar', subtitulo: 'Quantidades cadastradas no consultório', somenteLeituraEmTexto: true },
    'revisao da solicitacao': { icone: 'pi pi-verified', subtitulo: 'Conferência final antes do envio', somenteLeituraEmTexto: true },
    'regras da solicitacao': { icone: 'pi pi-verified', subtitulo: 'Condições que a solicitação precisa atender', somenteLeituraEmTexto: true },
    'validacao do periodo': { icone: 'pi pi-calendar-clock', subtitulo: 'Conferência das datas informadas', somenteLeituraEmTexto: true },

    // Quem solicita
    'dados do solicitante': { icone: 'pi pi-user', subtitulo: 'Identificação e contato de quem abriu o protocolo' },
    solicitante: { icone: 'pi pi-user', subtitulo: 'Identificação e contato de quem abriu o protocolo' },
    identificacao: { icone: 'pi pi-id-card', subtitulo: 'Nome, documentos e dados de identificação' },
    filiacao: { icone: 'pi pi-users', subtitulo: 'Nomes do pai e da mãe' },
    'nascimento e naturalidade': { icone: 'pi pi-map-marker', subtitulo: 'Data de nascimento e local de origem' },
    'dados complementares': { icone: 'pi pi-info-circle', subtitulo: 'Informações adicionais do cadastro' },
    'dados do medico falecido': { icone: 'pi pi-user-minus', subtitulo: 'Identificação do médico e data do óbito' },

    // Empresa / prestador
    prestador: { icone: 'pi pi-building', subtitulo: 'Razão social, CNPJ e identificação da empresa' },
    'dados prestador': { icone: 'pi pi-building', subtitulo: 'Razão social, CNPJ e identificação da empresa' },
    'prestador e indicacao': { icone: 'pi pi-building', subtitulo: 'Empresa e o vínculo indicado' },
    'natureza juridica e classificacao': { icone: 'pi pi-sitemap', subtitulo: 'Enquadramento jurídico e classificação da empresa' },
    'dados matriz e mantenedora': { icone: 'pi pi-briefcase', subtitulo: 'Vínculo com matriz e mantenedora' },
    'corpo societario': { icone: 'pi pi-users', subtitulo: 'Sócios registrados na empresa' },
    'composicao da comissao de etica': { icone: 'pi pi-users', subtitulo: 'Membros que compõem a comissão' },

    // Diretor técnico / clínico
    'diretor tecnico': { icone: 'pi pi-user', subtitulo: 'Médico responsável técnico pela empresa' },
    'dados diretor tecnico': { icone: 'pi pi-user', subtitulo: 'Médico responsável técnico pela empresa' },
    'diretor tecnico solicitante': { icone: 'pi pi-user', subtitulo: 'Médico que assina a solicitação' },
    'diretor tecnico atual': { icone: 'pi pi-user', subtitulo: 'Responsável técnico registrado hoje' },
    'diretor clinico atual': { icone: 'pi pi-user', subtitulo: 'Responsável clínico registrado hoje' },
    'diretor clinico novo': { icone: 'pi pi-user-plus', subtitulo: 'Responsável clínico que assume a função' },

    // Endereço e contato
    endereco: { icone: 'pi pi-map-marker', subtitulo: 'Logradouro, município e UF' },
    'endereco para correspondencia': { icone: 'pi pi-envelope', subtitulo: 'Endereço usado para envio de documentos' },
    'preferencias de endereco': { icone: 'pi pi-map-marker', subtitulo: 'Qual endereço vale para cada finalidade' },
    'contato e endereco': { icone: 'pi pi-map-marker', subtitulo: 'Onde encontrar e como falar com o solicitante' },
    contato: { icone: 'pi pi-phone', subtitulo: 'Telefones e e-mails de contato' },
    telefone: { icone: 'pi pi-phone', subtitulo: 'Telefones informados' },
    'telefones relacionados ao endereco': { icone: 'pi pi-phone', subtitulo: 'Telefones vinculados a este endereço' },
    comunicacao: { icone: 'pi pi-envelope', subtitulo: 'Canais para receber avisos do Conselho' },

    // Consultório
    'dados do consultorio': { icone: 'pi pi-building', subtitulo: 'CNES, uso e situação do consultório' },
    'horarios de funcionamento do consultorio': { icone: 'pi pi-clock', subtitulo: 'Dias e horários de atendimento' },
    compartilhamento: { icone: 'pi pi-users', subtitulo: 'Médicos que dividem o consultório' },

    // Registro, licenças e documentos
    'licencas e alvara': { icone: 'pi pi-file-check', subtitulo: 'Registro sanitário e alvará de funcionamento' },
    'registro e licencas': { icone: 'pi pi-file-check', subtitulo: 'Registro sanitário e alvará de funcionamento' },
    'registro e diploma': { icone: 'pi pi-file-check', subtitulo: 'Registro profissional e diploma apresentado' },
    documento: { icone: 'pi pi-file', subtitulo: 'Documento apresentado na solicitação' },
    documentacao: { icone: 'pi pi-file', subtitulo: 'Documentos apresentados na solicitação' },
    'documento comprobatorio': { icone: 'pi pi-file-check', subtitulo: 'Comprovação exigida para o pedido' },
    'documentacao pendente': { icone: 'pi pi-exclamation-triangle', subtitulo: 'O que ainda falta ser entregue' },
    'carteira de identidade': { icone: 'pi pi-id-card', subtitulo: 'Dados do documento de identidade' },
    cpf: { icone: 'pi pi-id-card', subtitulo: 'Cadastro de pessoa física' },
    'titulo de eleitor': { icone: 'pi pi-id-card', subtitulo: 'Dados do título eleitoral' },
    'registro nacional de estrangeiros': { icone: 'pi pi-id-card', subtitulo: 'Documento de estrangeiro registrado no país' },
    'informacoes militares': { icone: 'pi pi-shield', subtitulo: 'Situação e categoria militar' },

    // Anexos
    anexo: { icone: 'pi pi-paperclip', subtitulo: 'Arquivo enviado com a solicitação' },
    anexos: { icone: 'pi pi-paperclip', subtitulo: 'Arquivos enviados com a solicitação' },
    'anexos disponiveis': { icone: 'pi pi-paperclip', subtitulo: 'Arquivos disponíveis para visualização' },

    // Formação, especialidades e atuação
    formacao: { icone: 'pi pi-graduation-cap', subtitulo: 'Formação acadêmica do médico' },
    'dados do curso': { icone: 'pi pi-graduation-cap', subtitulo: 'Curso, instituição e período' },
    'dados da pos-graduacao': { icone: 'pi pi-graduation-cap', subtitulo: 'Programa de pós-graduação cursado' },
    especialidades: { icone: 'pi pi-tags', subtitulo: 'Especialidades registradas' },
    'especialidades solicitadas': { icone: 'pi pi-tags', subtitulo: 'Especialidades pedidas neste protocolo' },
    'area de atuacao': { icone: 'pi pi-map', subtitulo: 'Áreas de atuação registradas' },
    'areas de atuacao solicitadas': { icone: 'pi pi-map', subtitulo: 'Áreas de atuação pedidas neste protocolo' },

    // Estrutura e serviços do prestador
    servicos: { icone: 'pi pi-th-large', subtitulo: 'Serviços oferecidos pelo prestador' },
    atividades: { icone: 'pi pi-list-check', subtitulo: 'Atividades executadas pelo prestador' },
    'recursos materiais': { icone: 'pi pi-box', subtitulo: 'Equipamentos e instalações declarados' },
    'recursos humanos': { icone: 'pi pi-user-plus', subtitulo: 'Profissionais declarados pelo prestador' },
    'corpo clinico': { icone: 'pi pi-id-card', subtitulo: 'Médicos vinculados ao prestador' },
    'informacoes assistenciais': { icone: 'pi pi-info-circle', subtitulo: 'Perfil de atendimento do prestador' },

    // Financeiro
    pagamento: { icone: 'pi pi-money-bill', subtitulo: 'Boleto, valor e vencimento' },
    'dados bancarios': { icone: 'pi pi-money-bill', subtitulo: 'Conta indicada para o pagamento' },
    descontos: { icone: 'pi pi-percentage', subtitulo: 'Abatimentos aplicados ao valor' },
    'dados do fare': { icone: 'pi pi-money-bill', subtitulo: 'Valores do fundo de assistência' },

    // Diversos
    'dados do programa': { icone: 'pi pi-clipboard', subtitulo: 'Escopo e vigência do programa' },
    'dados do evento': { icone: 'pi pi-calendar', subtitulo: 'Evento que motivou a solicitação' },
    'periodo e finalidade': { icone: 'pi pi-calendar-clock', subtitulo: 'Prazo pedido e para que serve' },
    renovacao: { icone: 'pi pi-refresh', subtitulo: 'Dados da renovação solicitada' },
    atualizacao: { icone: 'pi pi-refresh', subtitulo: 'O que muda no cadastro' },
    entrega: { icone: 'pi pi-send', subtitulo: 'Como e onde o documento será entregue' },
    origem: { icone: 'pi pi-directions', subtitulo: 'Conselho e inscrição de origem' },
    adaptado: { icone: 'pi pi-wrench', subtitulo: 'Adaptações declaradas' },
    'tipo deficiencia': { icone: 'pi pi-heart', subtitulo: 'Deficiência informada pelo solicitante' },
    'outras*': { icone: 'pi pi-ellipsis-h', subtitulo: 'Demais informações declaradas' },
    'json de atualizacao': { icone: 'pi pi-code', subtitulo: 'Payload enviado ao portal' }
};

/** Ícone do separador da seção. Título desconhecido cai num ícone neutro, nunca em seção sem ícone. */
export function iconeDaSecao(titulo: string): string {
    return (APRESENTACAO[chave(titulo)] ?? PADRAO).icone;
}

/** Descrição curta sob o título. Vazia no título desconhecido — melhor sem subtítulo do que com um errado. */
export function subtituloDaSecao(titulo: string): string {
    return (APRESENTACAO[chave(titulo)] ?? PADRAO).subtitulo;
}

/**
 * A seção descreve DADOS do protocolo (campos) ou identificação/resumo (texto)?
 *
 * O padrão é `true`: seção nova nasce como dados. As exceções estão listadas uma a uma — são poucas
 * e a lista deixa explícito por que cada uma ficou de fora.
 */
export function secaoEhDeDados(titulo: string): boolean {
    return !(APRESENTACAO[chave(titulo)] ?? PADRAO).somenteLeituraEmTexto;
}
